import React, { forwardRef, useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { Button } from 'ncoded-component-library';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import { DataRoomRFI } from 'router/subrouters/DataRoom/models/DataRoomRfi';
import { NOTIFICATION_MEDIUM } from 'models/Notification';
import { getCheckedValuesInObject } from 'utils';
import useCallbackRef from 'hooks/useCallbackRef';
import { useRfi } from 'hooks/useRfi';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import CheckboxFieldGroup from 'components/CheckboxFieldGroup';
import DatepickerField from 'components/DatepickerField';
import ToggleField from 'components/ToggleField';
import RfiInfo from '../RfiInfo';
import RfiGrantFileAccessModal from './components/RfiGrantFileAccessModal';
import RfiRecipients from './components/RfiRecipients';
import { getIsRfiCreationTypeChecklist } from '../../../RfiList/components/RfiRequestsTable/utils';

import './RfiSettingsDrawer.styles.scss';

type RfiSettingsDrawerProps = {
  className?: string;
  rfi?: DataRoomRFI;
  onRfiUpdate: (rfi: DataRoomRFI) => void;
};

const RfiSettingsDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  RfiSettingsDrawerProps
> = ({ className, rfi, onRfiUpdate }, ref) => {
  const classes = classNames('rfi-settings-drawer', className);

  const {
    _id,
    dueDate,
    twentyFourHoursBeforeDeadlineReminder,
    atDeadlineReminder,
    threeDaysAfterDeadlineReminder,
    fiveDaysAfterDeadlineReminder,
    automaticFileUploadToDataRoom,
    notificationMedium = [],
    grantAccessDataRoomMemberIds = [],
    creationType,
  } = rfi || {};

  const { t } = useTranslation();
  const { popSuccess, popServerError } = useContext(PopNotificationsContext);
  const { notificationMediumOptions, getDueDateReminderOptions } = useRfi();
  const [membersModal, membersModalRef] = useCallbackRef<ModalRef>();

  const initialValues = useMemo(
    () => ({
      dueDate,
      twentyFourHoursBeforeDeadlineReminder,
      atDeadlineReminder,
      threeDaysAfterDeadlineReminder,
      fiveDaysAfterDeadlineReminder,
      automaticFileUploadToDataRoom,
      notificationMedium: {
        [NOTIFICATION_MEDIUM.EMAIL]: notificationMedium.includes(
          NOTIFICATION_MEDIUM.EMAIL,
        ),
        [NOTIFICATION_MEDIUM.SMS]: notificationMedium.includes(
          NOTIFICATION_MEDIUM.SMS,
        ),
        [NOTIFICATION_MEDIUM.PLATFORM]: notificationMedium.includes(
          NOTIFICATION_MEDIUM.PLATFORM,
        ),
      },
    }),
    [
      atDeadlineReminder,
      automaticFileUploadToDataRoom,
      dueDate,
      fiveDaysAfterDeadlineReminder,
      notificationMedium,
      threeDaysAfterDeadlineReminder,
      twentyFourHoursBeforeDeadlineReminder,
    ],
  );

  const onSubmit = useCallback(
    async (values) => {
      const { notificationMedium, ...rest } = values;

      const notificationMediumFinal =
        getCheckedValuesInObject(notificationMedium);

      try {
        const { data } = await dataRoomApi.rfi.patchRfi(_id, {
          notifyOnComplete: !!notificationMediumFinal.length,
          notificationMedium: notificationMediumFinal,
          ...rest,
        });

        popSuccess(t('succesfullyUpdatedRFI'));
        onRfiUpdate(data);
      } catch (e) {
        popServerError(e);
      }
    },
    [_id, onRfiUpdate, popServerError, popSuccess, t],
  );

  return (
    <SideDrawer className={classes} ref={ref} title={<RfiInfo rfi={rfi} />}>
      <Form
        keepDirtyOnReinitialize
        initialValues={initialValues}
        onSubmit={onSubmit}
        render={({ handleSubmit, submitting, pristine, values }) => (
          <form onSubmit={handleSubmit}>
            <Field
              name="dueDate"
              label={t('dueDate')}
              component={DatepickerField}
            />

            <div className="rfi-settings-drawer__checkboxes-group">
              <p className="rfi-settings-drawer__label">
                {t('dueDateRemindersFrequency')}
              </p>
              <div className="rfi-settings-drawer__checkboxes">
                {getDueDateReminderOptions(!values.dueDate)}
              </div>
            </div>

            <CheckboxFieldGroup
              name="notificationMedium"
              label={t('notificationMediums')}
              options={notificationMediumOptions}
            />

            <div className="rfi-settings-drawer__row">
              <div>
                <p className="rfi-settings-drawer__label">{t('autoUpload')}</p>
                <p className="rfi-settings-drawer__description">
                  {t('autoUploadDescription')}
                </p>
              </div>

              <Field
                name="automaticFileUploadToDataRoom"
                component={ToggleField}
                type="checkbox"
              />
            </div>

            <div className="rfi-settings-drawer__row">
              <div>
                <p className="rfi-settings-drawer__label">
                  {t('grantFileAccess')}
                </p>
                <p className="rfi-settings-drawer__description">
                  {t('chooseDataRoomMembersGrantAccess')}
                </p>
              </div>

              <Button variant="outline" onClick={() => membersModal?.open()}>
                {t('manage')}
              </Button>
            </div>

            {getIsRfiCreationTypeChecklist(creationType) && (
              <RfiRecipients rfi={rfi} onRfiUpdate={onRfiUpdate} />
            )}

            <div className="rfi-settings-drawer__actions">
              <Button type="submit" disabled={pristine || submitting}>
                {t('saveChanges')}
              </Button>
            </div>
          </form>
        )}
      />

      <RfiGrantFileAccessModal
        ref={membersModalRef}
        dataRoomRfiId={_id}
        grantAccessDataRoomMemberIds={grantAccessDataRoomMemberIds}
        onRfiUpdate={onRfiUpdate}
      />
    </SideDrawer>
  );
};

export default forwardRef(RfiSettingsDrawer);
